import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Clock } from "lucide-react";

export const trialLabel = (t) => {
  if (t.converted) return "Converted";
  if (t.expired) return "Trial ended";
  if (t.exhausted) return "Trial reviews used";
  return `${t.days_remaining} day${t.days_remaining === 1 ? "" : "s"} left`;
};
const trialTone = (t) => {
  if (t.blocked) return "bg-red-500/10 border-red-500/40 text-red-200";
  return t.show_nudge ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200";
};
function TrialMessage({ t }) {
  if (t.blocked) return <p className="text-red-300" data-testid="trial-blocked-message">Your trial {t.expired ? "has ended" : "reviews are used up"}. Everything you created is preserved and viewable — subscribe below to run new reviews and start projects.</p>;
  if (t.show_nudge) return <p className="text-amber-300" data-testid="trial-nudge-message">Your trial ends soon. Subscribe to keep reviewing without interruption.</p>;
  return <p className="text-slate-400">Trial reviews don't renew. Subscribe any time — your projects, findings and playbooks carry over.</p>;
}

const pct = (a, b) => Math.min(100, b ? (a / b) * 100 : 0);

function UsageBar({ label, used, limit, testId, alertAtLimit }) {
  const full = alertAtLimit && used >= limit;
  return (
    <div>
      <div className="flex justify-between"><span className="text-slate-400">{label}</span><span className="font-mono text-slate-200" data-testid={testId}>{used} / {limit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded mt-1"><div className={`h-1.5 rounded ${full ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct(used, limit)}%` }} /></div>
    </div>
  );
}

function TrialOffer({ rules: r, busy, onStart }) {
  return (
    <div className="sc-card p-5 mb-6 border-emerald-500/40" data-testid="trial-offer-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="label-cap text-emerald-400 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Fabricator free trial</div>
          <div className="font-heading text-xl font-extrabold text-slate-100 mt-1">{r.days} days free · no credit card</div>
          <div className="text-sm text-slate-400 mt-1">Same AI quality as the paid Fabricator plan. Includes {r.active_projects} active projects and {r.ai_reviews} AI reviews. One trial per company — your work is kept when it ends.</div>
        </div>
        <button disabled={busy} onClick={onStart} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-40" data-testid="start-trial-button">Start free trial</button>
      </div>
    </div>
  );
}

function TrialStatus({ t, admin }) {
  return (
    <div className={`sc-card p-5 mb-6 ${t.blocked ? "border-red-500/50" : "border-emerald-500/40"}`} data-testid="trial-status-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[260px] flex-1">
          <div className="label-cap text-emerald-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Fabricator free trial</div>
          <div className="font-heading text-xl font-extrabold text-slate-100 mt-1" data-testid="trial-days-remaining">{trialLabel(t)}</div>
          <div className="text-xs text-slate-500 font-mono mt-0.5">Ends {new Date(t.ends_at).toLocaleDateString()}</div>
          <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
            <UsageBar label="AI reviews" used={t.reviews_used} limit={t.reviews_limit} testId="trial-reviews-used" alertAtLimit />
            <UsageBar label="Active projects" used={t.projects_used} limit={t.projects_limit} testId="trial-projects-used" />
          </div>
        </div>
        <div className="text-sm max-w-sm">
          <TrialMessage t={t} />
          {admin && <a href="#plans" className="inline-block mt-2 text-amber-400 underline text-sm" data-testid="trial-subscribe-link">Subscribe to continue</a>}
        </div>
      </div>
    </div>
  );
}

export function TrialCard({ workspaceId, admin, onChanged }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/workspaces/${workspaceId}/billing/trial`).then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { if (workspaceId) load(); }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try { await api.post(`/workspaces/${workspaceId}/billing/trial/start`); toast.success("Your free trial is active — no card needed"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start trial"); } finally { setBusy(false); }
  };

  if (!info) return null;
  const { trial: t, rules } = info;
  if (!t) return info.eligible && admin ? <TrialOffer rules={rules} busy={busy} onStart={start} /> : null;
  if (t.converted) return null;
  return <TrialStatus t={t} admin={admin} />;
}

export function TrialBanner({ workspaceId }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    const go = () => api.get(`/workspaces/${workspaceId}/billing/trial`).then((r) => setT(r.data.trial)).catch(() => setT(null));
    go();
    const iv = setInterval(go, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [workspaceId]);
  if (!t || t.converted) return null;
  const tone = trialTone(t);
  return (
    <div className={`border-b px-4 py-1.5 text-xs flex flex-wrap items-center justify-between gap-2 ${tone}`} data-testid="trial-banner">
      <span className="font-mono"><b>Free trial</b> · {trialLabel(t)} · {t.reviews_used}/{t.reviews_limit} reviews · {t.projects_used}/{t.projects_limit} projects</span>
      {(t.blocked || t.show_nudge) && <Link to="/billing#plans" className="font-semibold underline" data-testid="trial-banner-subscribe">Subscribe to continue</Link>}
    </div>
  );
}
